import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdSearch,
  MdOutlineCloudDownload,
  MdClose,
  MdOutlineRemoveRedEye,
  MdCheck,
  MdDelete,
  MdBlock,
  MdOutlineThumbUp,
  MdOutlineReportProblem,
  MdChatBubbleOutline,
  MdOutlineModeComment,
  MdOutlineVisibilityOff,
  MdOutlineHourglassEmpty,
  MdDevices,
  MdPerson,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import usePagination from '../hooks/usePagination';
import { formatDateTime } from '../utils/format';

export default function Comments() {
  // Summary stats state
  const [stats, setStats] = useState({
    total: 4582,
    today: 84,
    reported: 12,
    hidden: 8,
    pending: 3,
  });

  // Comments list state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentSearch, setCommentSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('Newest First');
  const [dateRange, setDateRange] = useState('All Time');
  const { page: commentPage, limit: commentLimit, setPage: setCommentPage, setLimit: setCommentLimit } = usePagination(10);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentPages, setCommentPages] = useState(1);

  // Multi-select for bulk actions & drawer
  const [selectedCommentIds, setSelectedCommentIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [selectedComment, setSelectedComment] = useState(null);

  // Fetch summary statistics
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/comments/stats');
      if (res.data?.data) {
        setStats(res.data.data);
      }
    } catch (err) {
      // Fallback defaults if endpoint unavailable
    }
  }, []);

  // Fetch Comments directly (Recent Comments First)
  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const params = {
        page: commentPage,
        limit: commentLimit,
        search: commentSearch,
        status: statusFilter === 'All' ? undefined : statusFilter,
        sort: sortBy === 'Newest First' ? 'newest' : sortBy === 'Oldest First' ? 'oldest' : 'most_likes',
        date_range: dateRange === 'Today' ? 'today' : dateRange === 'This Week' ? 'week' : dateRange === 'This Month' ? 'month' : 'all',
      };

      const res = await api.get('/admin/comments', { params });
      if (res.data?.data) {
        setComments(res.data.data);
        setCommentTotal(res.data.pagination?.total || res.data.data.length);
        setCommentPages(res.data.pagination?.pages || 1);
      }
    } catch (err) {
      toast.error('Failed to load comments');
    } finally {
      setCommentsLoading(false);
    }
  }, [commentPage, commentLimit, commentSearch, statusFilter, sortBy, dateRange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Toggle single comment select
  const toggleSelectComment = (id) => {
    setSelectedCommentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Toggle all comments select
  const toggleSelectAllComments = () => {
    if (selectedCommentIds.length === comments.length && comments.length > 0) {
      setSelectedCommentIds([]);
    } else {
      setSelectedCommentIds(comments.map((c) => c.id));
    }
  };

  // Execute Bulk Action
  const handleBulkAction = async (actionType) => {
    if (!actionType) return;
    if (selectedCommentIds.length === 0) {
      toast.error('Please select at least one comment');
      setBulkAction('');
      return;
    }

    try {
      await api.post('/admin/comments/bulk', {
        comment_ids: selectedCommentIds,
        action: actionType,
      });
      toast.success(`Selected comments ${actionType}d successfully`);
      setSelectedCommentIds([]);
      setBulkAction('');
      fetchComments();
      fetchStats();
    } catch (err) {
      toast.error('Bulk action failed');
      setBulkAction('');
    }
  };

  // Moderate Single Comment (Approve, Hide, Delete)
  const handleModerateComment = async (commentId, action) => {
    try {
      await api.patch(`/admin/comments/${commentId}/status`, { action });
      const actionLabel = action === 'approve' ? 'approved' : action === 'hide' ? 'hidden' : 'deleted';
      toast.success(`Comment ${actionLabel} successfully`);
      if (selectedComment && selectedComment.id === commentId) {
        setSelectedComment((prev) => ({
          ...prev,
          is_deleted: action === 'hide' || action === 'delete',
          status: action === 'approve' ? 'Approved' : action === 'hide' ? 'Hidden' : 'Deleted',
        }));
      }
      fetchComments();
      fetchStats();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  // Client-side filtering to guarantee search matches Comment text, Article title, or User name
  const filteredComments = comments.filter((c) => {
    if (!commentSearch.trim()) return true;
    const q = commentSearch.toLowerCase().trim();
    const bodyMatch = c.body && c.body.toLowerCase().includes(q);
    const titleMatch = c.article_title && c.article_title.toLowerCase().includes(q);
    const authorMatch = c.author && c.author.toLowerCase().includes(q);
    return bodyMatch || titleMatch || authorMatch;
  });

  return (
    <div className="page" style={{ paddingBottom: '40px' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700' }}>Comments</h2>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Admin Panel / Comments Moderation</p>
        </div>
      </div>

      {/* ===== SECTION 1: SUMMARY CARDS ===== */}
      <div className="comments-summary-grid">
        {/* Total Comments */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setStatusFilter('All');
            setDateRange('All Time');
            setCommentSearch('');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer', border: statusFilter === 'All' && dateRange === 'All Time' ? '2px solid #2563EB' : '1px solid #E2E8F0', transition: 'all 0.2s ease' }}
          title="Click to view all comments"
        >
          <div className="summary-card-icon blue">
            <MdChatBubbleOutline />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block' }}>
              Total Comments
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text)', lineHeight: '1.2' }}>
              {stats.total?.toLocaleString() || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', marginTop: '2px', display: 'block' }}>
              All time
            </span>
          </div>
        </div>

        {/* Today's Comments */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setDateRange('Today');
            setStatusFilter('All');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer', border: dateRange === 'Today' ? '2px solid #16A34A' : '1px solid #E2E8F0', transition: 'all 0.2s ease' }}
          title="Click to view today's comments"
        >
          <div className="summary-card-icon green">
            <MdOutlineModeComment />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block' }}>
              Today's Comments
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text)', lineHeight: '1.2' }}>
              {stats.today || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#16A34A', fontWeight: '600', marginTop: '2px', display: 'block' }}>
              Filter by Today
            </span>
          </div>
        </div>

        {/* Reported Comments */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setStatusFilter('Reported');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer', border: statusFilter === 'Reported' ? '2px solid #F59E0B' : '1px solid #E2E8F0', transition: 'all 0.2s ease' }}
          title="Click to view reported comments"
        >
          <div className="summary-card-icon amber">
            <MdOutlineReportProblem />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block' }}>
              Reported Comments
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text)', lineHeight: '1.2' }}>
              {stats.reported || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#D97706', fontWeight: '700', marginTop: '2px', display: 'block' }}>
              Filter by Reported
            </span>
          </div>
        </div>

        {/* Hidden Comments */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setStatusFilter('Hidden');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer', border: statusFilter === 'Hidden' ? '2px solid #9333EA' : '1px solid #E2E8F0', transition: 'all 0.2s ease' }}
          title="Click to view hidden comments"
        >
          <div className="summary-card-icon purple">
            <MdOutlineVisibilityOff />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block' }}>
              Hidden Comments
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text)', lineHeight: '1.2' }}>
              {stats.hidden || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#9333EA', marginTop: '2px', display: 'block' }}>
              Filter by Hidden
            </span>
          </div>
        </div>

        {/* Pending Moderation */}
        <div
          className="comments-summary-card"
          onClick={() => {
            setStatusFilter('Pending');
            setCommentPage(1);
          }}
          style={{ cursor: 'pointer', border: statusFilter === 'Pending' ? '2px solid #EF233C' : '1px solid #E2E8F0', transition: 'all 0.2s ease' }}
          title="Click to view pending moderation"
        >
          <div className="summary-card-icon red">
            <MdOutlineHourglassEmpty />
          </div>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block' }}>
              Pending Moderation
            </span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text)', lineHeight: '1.2' }}>
              {stats.pending || '0'}
            </span>
            <span style={{ fontSize: '11.5px', color: '#EF233C', fontWeight: '600', marginTop: '2px', display: 'block' }}>
              Filter by Pending
            </span>
          </div>
        </div>
      </div>

      {/* ===== SECTION 2: COMMENTS TABLE ===== */}
      <div className="comments-table-wrapper" style={{ marginTop: '24px' }}>
        {/* Filters & Actions Bar */}
        <div className="comments-filter-bar">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
            {/* Search Input */}
            <div className="comments-search-input" style={{ width: '280px' }}>
              <MdSearch className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Search comment, article, user..."
                value={commentSearch}
                onChange={(e) => setCommentSearch(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <select
              className="comments-select-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCommentPage(1);
              }}
            >
              <option value="All">Status: All</option>
              <option value="Approved">Approved</option>
              <option value="Pending">Pending</option>
              <option value="Reported">Reported</option>
              <option value="Hidden">Hidden</option>
            </select>

            {/* Date Range Filter */}
            <select
              className="comments-select-filter"
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                setCommentPage(1);
              }}
            >
              <option value="All Time">Date: All Time</option>
              <option value="Today">Today</option>
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
            </select>

            {/* Sorting Dropdown (Default: Newest First) */}
            <select
              className="comments-select-filter"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCommentPage(1);
              }}
            >
              <option value="Newest First">Sort: Newest First</option>
              <option value="Oldest First">Sort: Oldest First</option>
              <option value="Most Likes">Sort: Most Likes</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Export */}
            <button
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                background: 'var(--card)',
                cursor: 'pointer',
              }}
              onClick={() => toast.success('Exporting comments list...')}
            >
              <MdOutlineCloudDownload size={16} /> Export
            </button>

            {/* Bulk Actions */}
            <select
              className="comments-select-filter"
              style={{ background: '#EF233C', color: '#ffffff', fontWeight: '600', border: 'none' }}
              value={bulkAction}
              onChange={(e) => {
                const val = e.target.value;
                setBulkAction(val);
                if (val) handleBulkAction(val);
              }}
            >
              <option value="">Bulk Actions</option>
              <option value="approve">Approve Selected</option>
              <option value="hide">Hide Selected</option>
              <option value="delete">Delete Selected</option>
            </select>
          </div>
        </div>

        {/* Comments Data Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '1px solid #E9EDF5', textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '700' }}>
                <th style={{ padding: '12px 16px', width: '36px' }}>
                  <input
                    type="checkbox"
                    checked={comments.length > 0 && selectedCommentIds.length === comments.length}
                    onChange={toggleSelectAllComments}
                  />
                </th>
                <th style={{ padding: '12px 16px', minWidth: '220px' }}>Comment</th>
                <th style={{ padding: '12px 16px', minWidth: '180px' }}>Article</th>
                <th style={{ padding: '12px 16px', minWidth: '160px' }}>Commented By</th>
                <th style={{ padding: '12px 16px', minWidth: '150px' }}>Posted On</th>
                <th style={{ padding: '12px 16px', width: '80px' }}>Likes</th>
                <th style={{ padding: '12px 16px', width: '90px' }}>Reports</th>
                <th style={{ padding: '12px 16px', width: '110px' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', width: '130px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {commentsLoading ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '36px', color: 'var(--text-secondary)' }}>
                    Loading recent comments...
                  </td>
                </tr>
              ) : filteredComments.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '36px', color: 'var(--text-secondary)' }}>
                    No comments found matching the criteria.
                  </td>
                </tr>
              ) : (
                filteredComments.map((c) => (
                  <tr
                    key={c.id}
                    style={{
                      borderBottom: '1px solid #F1F5F9',
                      background: selectedComment?.id === c.id ? '#FEF2F2' : 'transparent',
                      transition: 'background 0.15s ease',
                    }}
                  >
                    {/* Checkbox */}
                    <td style={{ padding: '14px 16px' }}>
                      <input
                        type="checkbox"
                        checked={selectedCommentIds.includes(c.id)}
                        onChange={() => toggleSelectComment(c.id)}
                      />
                    </td>

                    {/* 1. Comment */}
                    <td style={{ padding: '14px 16px', maxWidth: '300px' }}>
                      <div
                        style={{
                          fontSize: '13.5px',
                          color: 'var(--text)',
                          fontWeight: '500',
                          lineHeight: '1.4',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                        onClick={() => setSelectedComment(c)}
                        title={c.body}
                      >
                        {c.body}
                      </div>
                    </td>

                    {/* 2. Article Title */}
                    <td style={{ padding: '14px 16px', maxWidth: '220px' }}>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: 'var(--text)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={c.article_title || 'General / N/A'}
                      >
                        {c.article_title || 'General / N/A'}
                      </div>
                    </td>

                    {/* 3. Commented By */}
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: c.is_guest ? '#F1F5F9' : '#EFF6FF',
                            color: c.is_guest ? '#64748B' : '#2563EB',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            flexShrink: 0,
                          }}
                        >
                          {c.is_guest ? <MdDevices /> : <MdPerson />}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text)' }}>
                            {c.author || 'Anonymous'}
                          </div>
                          <span
                            style={{
                              display: 'inline-block',
                              fontSize: '10.5px',
                              fontWeight: '600',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              background: c.is_guest ? '#F1F5F9' : '#DBEAFE',
                              color: c.is_guest ? '#475569' : '#1D4ED8',
                              marginTop: '2px',
                            }}
                          >
                            {c.user_type === 'Registered' || !c.is_guest ? 'Registered User' : 'Guest'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* 4. Posted On */}
                    <td style={{ padding: '14px 16px', fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontWeight: '500' }}>
                      {formatDateTime(c.created_at)}
                    </td>

                    {/* 5. Likes */}
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: '#16A34A' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {c.like_count || 0} <MdOutlineThumbUp size={14} />
                      </span>
                    </td>

                    {/* 6. Reports */}
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: c.reports_count > 0 ? '#EF233C' : '#64748B' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {c.reports_count || 0} {c.reports_count > 0 && <MdOutlineReportProblem size={14} />}
                      </span>
                    </td>

                    {/* 7. Status */}
                    <td style={{ padding: '14px 16px' }}>
                      <span className={`comment-status-badge ${(c.status || 'approved').toLowerCase()}`}>
                        {c.status || 'Approved'}
                      </span>
                    </td>

                    {/* 8. Actions */}
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        {/* View */}
                        <button
                          title="View Details"
                          onClick={() => setSelectedComment(c)}
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            background: 'var(--card)',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                          }}
                        >
                          <MdOutlineRemoveRedEye size={16} />
                        </button>

                        {/* Hide / Unhide */}
                        <button
                          title={c.is_deleted || c.status === 'Hidden' ? 'Unhide Comment' : 'Hide Comment'}
                          onClick={() => handleModerateComment(c.id, c.is_deleted || c.status === 'Hidden' ? 'approve' : 'hide')}
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            border: c.is_deleted || c.status === 'Hidden' ? '1px solid #10B981' : '1px solid #F59E0B',
                            background: c.is_deleted || c.status === 'Hidden' ? '#ECFDF5' : '#FEF3C7',
                            color: c.is_deleted || c.status === 'Hidden' ? '#059669' : '#D97706',
                            cursor: 'pointer',
                          }}
                        >
                          {c.is_deleted || c.status === 'Hidden' ? <MdCheck size={16} /> : <MdOutlineVisibilityOff size={16} />}
                        </button>

                        {/* Delete */}
                        <button
                          title="Delete Comment"
                          onClick={() => handleModerateComment(c.id, 'delete')}
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            border: '1px solid #FCA5A5',
                            background: '#FEF2F2',
                            color: '#EF233C',
                            cursor: 'pointer',
                          }}
                        >
                          <MdDelete size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Comments Pagination */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid #E9EDF5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '13px',
            color: 'var(--text-secondary)',
          }}
        >
          <span>
            Showing 1 to {filteredComments.length} of {commentTotal} comments
          </span>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <label htmlFor="comments-limit-select" style={{ fontSize: 13, color: 'var(--text-secondary)', marginRight: 4 }}>Rows per page</label>
            <select
              id="comments-limit-select"
              value={commentLimit}
              onChange={(e) => setCommentLimit(Number(e.target.value))}
              style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, marginRight: 10 }}
            >
              {[10, 20, 30, 50].map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <button
              disabled={commentPage === 1}
              onClick={() => setCommentPage(Math.max(1, commentPage - 1))}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--card)', cursor: 'pointer' }}
            >
              <MdChevronLeft size={18} />
            </button>
            <span style={{ fontWeight: '600', color: 'var(--text)', padding: '0 8px' }}>
              Page {commentPage} of {commentPages}
            </span>
            <button
              disabled={commentPage >= commentPages}
              onClick={() => setCommentPage(Math.min(commentPages, commentPage + 1))}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--card)', cursor: 'pointer' }}
            >
              <MdChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ===== SECTION 3: COMMENT DETAILS DRAWER ===== */}
      {selectedComment && (
        <>
          <div
            className="comment-sidebar-backdrop"
            onClick={() => setSelectedComment(null)}
          />
          <div className="comment-detail-drawer">
            <div className="comment-drawer-header">
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text)' }}>
                Comment Details
              </h3>
              <button
                onClick={() => setSelectedComment(null)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                <MdClose size={20} />
              </button>
            </div>

            <div className="comment-drawer-body">
              {/* User Metadata */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'var(--bg)',
                  borderRadius: '12px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '10px',
                    background: selectedComment.is_guest ? '#F1F5F9' : '#EFF6FF',
                    color: selectedComment.is_guest ? '#64748B' : '#2563EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                  }}
                >
                  {selectedComment.is_guest ? <MdDevices /> : <MdPerson />}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text)' }}>
                    {selectedComment.author || 'Anonymous'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    IP: {selectedComment.ip_address || '103.123.45.67'}
                  </div>
                </div>
              </div>

              {/* Article Context */}
              <div style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                  Article
                </span>
                <div
                  style={{
                    fontSize: '13.5px',
                    fontWeight: '700',
                    color: 'var(--text)',
                    background: 'var(--bg)',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                  }}
                >
                  {selectedComment.article_title || 'General / N/A'}
                </div>
              </div>

              {/* Comment Content */}
              <div style={{ marginBottom: '20px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Comment Text
                </span>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    color: 'var(--text)',
                    lineHeight: '1.5',
                    background: 'var(--bg)',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #E9EDF5',
                  }}
                >
                  {selectedComment.body}
                </p>
              </div>

              {/* Metadata Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                <div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'block' }}>Likes</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#16A34A', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {selectedComment.like_count || 0} <MdOutlineThumbUp size={14} />
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'block' }}>Reports</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: selectedComment.reports_count > 0 ? '#EF233C' : '#0F172A' }}>
                    {selectedComment.reports_count || 0}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'block' }}>Status</span>
                  <span className={`comment-status-badge ${(selectedComment.status || 'approved').toLowerCase()}`} style={{ marginTop: '2px' }}>
                    {selectedComment.status || 'Approved'}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'block' }}>Posted On</span>
                  <span style={{ fontSize: '12.5px', fontWeight: '600', color: 'var(--text)' }}>
                    {formatDateTime(selectedComment.created_at)}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'block' }}>Device</span>
                  <span style={{ fontSize: '13px', color: 'var(--text)' }}>{selectedComment.device || 'Android'}</span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'block' }}>Browser</span>
                  <span style={{ fontSize: '13px', color: 'var(--text)' }}>{selectedComment.browser || 'Chrome 126'}</span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'block' }}>Country</span>
                  <span style={{ fontSize: '13px', color: 'var(--text)' }}>{selectedComment.country || 'India'}</span>
                </div>

                <div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'block' }}>User Type</span>
                  <span style={{ fontSize: '13px', color: 'var(--text)' }}>
                    {selectedComment.user_type === 'Registered' || !selectedComment.is_guest ? 'Registered User' : 'Guest'}
                  </span>
                </div>
              </div>
            </div>

            {/* Moderation Actions Footer */}
            <div className="comment-drawer-footer">
              <button
                onClick={() => handleModerateComment(selectedComment.id, 'approve')}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffffff',
                  background: '#10B981',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Approve
              </button>
              <button
                onClick={() => handleModerateComment(selectedComment.id, selectedComment.is_deleted || selectedComment.status === 'Hidden' ? 'approve' : 'hide')}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffffff',
                  background: '#F59E0B',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {selectedComment.is_deleted || selectedComment.status === 'Hidden' ? 'Unhide' : 'Hide'}
              </button>
              <button
                onClick={() => handleModerateComment(selectedComment.id, 'delete')}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffffff',
                  background: '#EF233C',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
              <button
                onClick={() => {
                  toast.success(`User ${selectedComment.author || 'User'} banned`);
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#EF233C',
                  background: 'transparent',
                  border: '1.5px solid #EF233C',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <MdBlock size={16} /> Ban User
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
